/**
 * Envelope Challenge v2 - Gamified Real-Time Collaborative Savings
 * FIXED VERSION WITH:
 * - Multi-step setup flow (Instructions → Options → Dashboard)
 * - Whole dollar amounts only
 * - Debugged invitation flow
 * - Notification opt-in prompt
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiGet, apiPost, getToken } from '@/utils/api';
import { toast } from 'sonner';
import { shareEnvelopeChallenge, canShare } from '@/utils/sharing';
import confetti from 'canvas-confetti';
import { useEnvelope } from '@/contexts/EnvelopeContext';
import ChallengeProgressGrid from '@/components/ChallengeProgressGrid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// ==================== HAPTIC FEEDBACK ====================
const triggerHaptic = async (style = 'medium') => {
  try {
    if (window.Capacitor?.isNativePlatform()) {
      const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
      await Haptics.impact({ style: style === 'heavy' ? ImpactStyle.Heavy : ImpactStyle.Medium });
    } else if (navigator.vibrate) {
      navigator.vibrate(style === 'heavy' ? 100 : 50);
    }
  } catch (e) {
    // Haptics not available
  }
};

// ==================== CONFETTI EXPLOSION ====================
const triggerConfetti = () => {
  const count = 200;
  const defaults = { origin: { y: 0.7 }, zIndex: 9999 };

  confetti({ ...defaults, spread: 26, startVelocity: 55, particleCount: 50, colors: ['#FFD700', '#00FF00'] });
  confetti({ ...defaults, spread: 60, particleCount: 40, colors: ['#FFD700', '#00FF00', '#00CED1'] });
  confetti({ ...defaults, spread: 100, decay: 0.91, scalar: 0.8, particleCount: 70 });
};

// ==================== STEP 1: INSTRUCTIONS SCREEN ====================
function InstructionsScreen({ onNext }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6 flex flex-col"
    >
      <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
          className="text-7xl mb-6"
        >
          📨
        </motion.div>
        
        <h1 className="text-3xl font-black text-white mb-4">
          The Envelope Challenge
        </h1>
        
        <p className="text-gray-300 text-lg mb-8 leading-relaxed">
          A fun way to save money by revealing a random amount each day and stacking your savings!
        </p>
        
        <div className="bg-white/10 rounded-2xl p-6 mb-8 text-left w-full">
          <h2 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
            <span>🎯</span> How It Works
          </h2>
          
          <ul className="space-y-4 text-gray-300">
            <li className="flex gap-3">
              <span className="text-2xl">1️⃣</span>
              <div>
                <p className="text-white font-medium">Set Your Goal</p>
                <p className="text-sm">Choose how much you want to save and how many days</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-2xl">2️⃣</span>
              <div>
                <p className="text-white font-medium">Daily Reveal</p>
                <p className="text-sm">Each day, tap to reveal a random savings amount</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-2xl">3️⃣</span>
              <div>
                <p className="text-white font-medium">Save & Stack</p>
                <p className="text-sm">Hit "I Saved It!" to build your streak and earn Street Cred</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="text-2xl">4️⃣</span>
              <div>
                <p className="text-white font-medium">No Pressure</p>
                <p className="text-sm">Can't save today? Skip it and catch up later from your Vault</p>
              </div>
            </li>
          </ul>
        </div>
        
        <div className="bg-amber-500/20 border border-amber-500/50 rounded-xl p-4 mb-8 w-full">
          <p className="text-amber-300 text-sm flex items-center gap-2">
            <span>💡</span>
            <span>Invite friends to compete! See who can keep the longest streak on the Guap Board 🔥</span>
          </p>
        </div>
        
        <motion.button
          onClick={onNext}
          className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold text-lg rounded-2xl shadow-lg"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Let's Set Up Your Challenge →
        </motion.button>
      </div>
    </motion.div>
  );
}

// ==================== STEP 2: OPTIONS/SETUP SCREEN ====================
function SetupScreen({ onBack, onCreateChallenge, isLoading }) {
  const [name, setName] = useState('');
  const [totalGoal, setTotalGoal] = useState('');
  const [duration, setDuration] = useState('');
  const [validationError, setValidationError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  
  const presets = [
    { label: '7 Days', duration: 7, goal: 100 },
    { label: '14 Days', duration: 14, goal: 250 },
    { label: '30 Days', duration: 30, goal: 500 },
    { label: '60 Days', duration: 60, goal: 1000 },
  ];
  
  // Validate that goal is divisible by duration for whole dollar daily amounts
  const validateWholeNumber = (goal, dur) => {
    const goalNum = parseInt(goal);
    const durNum = parseInt(dur);
    
    if (!goalNum || !durNum || durNum <= 0) {
      setValidationError(null);
      setSuggestions([]);
      return;
    }
    
    const daily = goalNum / durNum;
    if (Number.isInteger(daily)) {
      setValidationError(null);
      setSuggestions([]);
      return;
    }
    
    // Find nearby values that result in whole numbers
    const findWholeSuggestions = () => {
      const results = [];
      // Check nearby goal values
      for (let offset = 1; offset <= 20; offset++) {
        const lowerGoal = goalNum - offset;
        const higherGoal = goalNum + offset;
        
        if (lowerGoal > 0 && lowerGoal % durNum === 0) {
          results.push({ type: 'goal', value: lowerGoal, daily: lowerGoal / durNum });
        }
        if (higherGoal % durNum === 0) {
          results.push({ type: 'goal', value: higherGoal, daily: higherGoal / durNum });
        }
        
        if (results.length >= 2) break;
      }
      return results.slice(0, 2);
    };
    
    const wholeSuggestions = findWholeSuggestions();
    setSuggestions(wholeSuggestions);
    setValidationError(`Daily amount ($${daily.toFixed(2)}) isn't a whole number.`);
  };
  
  // Handle goal input - keep as string to allow empty state
  const handleGoalChange = (e) => {
    const value = e.target.value;
    // Only allow digits
    if (value === '' || /^\d+$/.test(value)) {
      setTotalGoal(value);
      validateWholeNumber(value, duration);
    }
  };
  
  // Handle duration input - keep as string to allow empty state
  const handleDurationChange = (e) => {
    const value = e.target.value;
    // Only allow digits, max 100
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= 100)) {
      setDuration(value);
      validateWholeNumber(totalGoal, value);
    }
  };
  
  const handleCreate = () => {
    const goalNum = parseInt(totalGoal);
    const durNum = parseInt(duration);
    
    if (!name.trim()) {
      toast.error('Please name your challenge');
      return;
    }
    if (!goalNum || !durNum) {
      toast.error('Please enter a savings goal and duration');
      return;
    }
    if (goalNum < durNum) {
      toast.error(`Goal must be at least $${durNum} (minimum $1 per day)`);
      return;
    }
    if (validationError) {
      toast.warning('Consider adjusting for whole dollar daily amounts');
    }
    onCreateChallenge({ name: name.trim(), total_goal: goalNum, duration: durNum });
  };
  
  const applySuggestion = (suggestion) => {
    if (suggestion.type === 'goal') {
      setTotalGoal(suggestion.value.toString());
      validateWholeNumber(suggestion.value.toString(), duration);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6"
    >
      <div className="max-w-md mx-auto">
        {/* Back button */}
        <button onClick={onBack} className="text-gray-400 mb-6 flex items-center gap-2">
          ← Back
        </button>
        
        <h1 className="text-2xl font-black text-white mb-2">
          ⚙️ Challenge Setup
        </h1>
        <p className="text-gray-400 mb-6">Configure your savings challenge</p>
        
        {/* Challenge Name */}
        <div className="mb-6">
          <label className="text-white font-medium mb-2 block">Challenge Name</label>
          <input
            type="text"
            placeholder="e.g., February Savings Sprint"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none"
          />
        </div>
        
        {/* Quick Presets */}
        <div className="mb-6">
          <label className="text-white font-medium mb-3 block">Quick Presets</label>
          <div className="grid grid-cols-2 gap-3">
            {presets.map((preset) => (
              <button
                key={preset.duration}
                onClick={() => {
                  setDuration(preset.duration.toString());
                  setTotalGoal(preset.goal.toString());
                  setValidationError(null);
                  setSuggestions([]);
                }}
                className={`py-3 px-4 rounded-xl border-2 transition-all ${
                  parseInt(duration) === preset.duration && parseInt(totalGoal) === preset.goal
                    ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                    : 'border-white/20 text-gray-400 hover:border-white/40'
                }`}
              >
                <div className="font-bold">{preset.label}</div>
                <div className="text-sm opacity-70">${preset.goal}</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Custom Goal */}
        <div className="mb-6">
          <label className="text-white font-medium mb-2 block">
            Target Savings Goal
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={totalGoal}
            onChange={handleGoalChange}
            placeholder="Enter amount"
            className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white text-xl font-bold focus:outline-none placeholder-gray-500 ${
              validationError 
                ? 'border-red-500 focus:border-red-400' 
                : 'border-white/20 focus:border-amber-500'
            }`}
          />
        </div>
        
        {/* Duration */}
        <div className="mb-4">
          <label className="text-white font-medium mb-2 block">
            Duration (Days)
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={duration}
            onChange={handleDurationChange}
            placeholder="Enter days (1-100)"
            className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white focus:outline-none placeholder-gray-500 ${
              validationError 
                ? 'border-red-500 focus:border-red-400' 
                : 'border-white/20 focus:border-amber-500'
            }`}
          />
          <p className="text-gray-500 text-sm mt-1">1-100 days</p>
        </div>
        
        {/* Validation Error & Suggestions */}
        {validationError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl"
          >
            <p className="text-red-300 text-sm mb-2">⚠️ {validationError}</p>
            {suggestions.length > 0 && (
              <div>
                <p className="text-red-200 text-xs mb-2">
                  To keep your daily savings as a whole dollar amount, try:
                </p>
                <div className="flex gap-2 flex-wrap">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => applySuggestion(s)}
                      className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      ${s.value} (${s.daily}/day)
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
        
        {/* Summary */}
        {totalGoal && duration && (
          <div className={`border rounded-xl p-4 mb-6 ${
            validationError 
              ? 'bg-amber-500/10 border-amber-500/30' 
              : 'bg-emerald-500/20 border-emerald-500/50'
          }`}>
            <p className={`text-sm mb-2 ${validationError ? 'text-amber-400' : 'text-emerald-400'}`}>
              📊 Your Challenge Summary:
            </p>
            <p className="text-white font-bold">
              Save ${Number(totalGoal).toLocaleString()} over {duration} days
            </p>
            <p className={`text-sm ${validationError ? 'text-amber-300' : 'text-emerald-300'}`}>
              Daily: ${(parseInt(totalGoal) / parseInt(duration)).toFixed(2)} (randomized amounts)
            </p>
          </div>
        )}
        
        {/* Create Button */}
        <motion.button
          onClick={handleCreate}
          disabled={isLoading || !name.trim() || !totalGoal || !duration}
          className="w-full py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold text-lg rounded-2xl shadow-lg disabled:opacity-50"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {isLoading ? 'Creating...' : '🚀 Start Challenge'}
        </motion.button>
      </div>
    </motion.div>
  );
}

// ==================== DAILY ENVELOPE ====================
function DailyEnvelope({ envelope, onReveal, onSave, onSkip, isRevealed, isLoading, todayStatus }) {
  const [isOpening, setIsOpening] = useState(false);
  const [showAmount, setShowAmount] = useState(isRevealed);

  const handleTapToOpen = async () => {
    if (isRevealed || isOpening) return;
    
    setIsOpening(true);
    triggerHaptic('light');
    
    setTimeout(async () => {
      await onReveal();
      setShowAmount(true);
      setIsOpening(false);
      triggerHaptic('medium');
    }, 800);
  };

  useEffect(() => {
    setShowAmount(isRevealed);
  }, [isRevealed]);

  // Display amount as whole dollar (no decimals)
  const displayAmount = Math.round(envelope?.amount || 0);

  return (
    <div className="flex flex-col items-center">
      <motion.div
        className="relative cursor-pointer"
        onClick={handleTapToOpen}
        whileTap={{ scale: 0.95 }}
      >
        <motion.div
          className={`w-64 h-40 rounded-2xl shadow-2xl overflow-hidden relative ${
            showAmount 
              ? 'bg-gradient-to-br from-emerald-400 to-emerald-600' 
              : 'bg-gradient-to-br from-amber-400 to-orange-500'
          }`}
          animate={isOpening ? { rotateY: [0, 180, 360], scale: [1, 1.1, 1] } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.div 
            className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-white/30 to-transparent"
            animate={isOpening ? { scaleY: [1, 0, 1] } : {}}
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 
              border-l-[60px] border-r-[60px] border-t-[30px] 
              border-l-transparent border-r-transparent border-t-white/40"
            />
          </motion.div>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {!showAmount ? (
              <motion.div className="text-center" animate={isOpening ? { opacity: [1, 0] } : {}}>
                <span className="text-5xl mb-2">📨</span>
                <p className="text-white font-bold text-lg">Tap to Reveal!</p>
                <p className="text-white/80 text-sm">Day {envelope?.id || '?'}</p>
              </motion.div>
            ) : (
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, type: 'spring' }}
              >
                <span className="text-4xl mb-1 block">💰</span>
                <p className="text-white font-black text-4xl">${displayAmount}</p>
                <p className="text-white/80 text-sm mt-1">Today's Envelope</p>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {showAmount && todayStatus === 'none' && (
          <motion.div 
            className="mt-6 flex gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <motion.button
              onClick={onSave}
              disabled={isLoading}
              className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold text-lg rounded-2xl shadow-lg shadow-emerald-500/30 disabled:opacity-50"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isLoading ? '...' : '✅ I Saved It!'}
            </motion.button>
            
            <motion.button
              onClick={onSkip}
              disabled={isLoading}
              className="px-6 py-4 bg-gray-200 text-gray-700 font-medium rounded-2xl disabled:opacity-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Skip Today
            </motion.button>
          </motion.div>
        )}

        {todayStatus === 'saved' && (
          <motion.div className="mt-6 text-center" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}>
            <span className="text-5xl">🎉</span>
            <p className="text-emerald-600 font-bold text-xl mt-2">You crushed it today!</p>
          </motion.div>
        )}

        {todayStatus === 'resting' && (
          <motion.div className="mt-6 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <span className="text-4xl">😴</span>
            <p className="text-gray-500 font-medium mt-2">Resting today - check your Vault</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ==================== GUAP BOARD ====================
function GuapBoard({ participants, currentUserId, challengeGoal = 500 }) {
  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-5 shadow-xl">
      <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
        <span>🏆</span> Guap Board
      </h3>
      
      <div className="space-y-4">
        {participants.map((p, idx) => {
          const progress = challengeGoal > 0 ? Math.min(((p.total_saved || 0) / challengeGoal) * 100, 100) : 0;
          const withdrawnProgress = challengeGoal > 0 && p.total_withdrawn ? Math.min((p.total_withdrawn / challengeGoal) * 100, 100) : 0;
          
          return (
            <motion.div
              key={p.user_id}
              className={`p-3 rounded-xl ${
                p.user_id === currentUserId 
                  ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/50' 
                  : 'bg-white/5'
              }`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
              layout
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                  </span>
                  <div>
                    <p className="text-white font-medium flex items-center gap-1">
                      {p.user_name}
                      {p.is_hot && <span className="text-orange-500">🔥</span>}
                      {p.user_id === currentUserId && (
                        <span className="text-xs bg-amber-500 text-black px-1.5 py-0.5 rounded-full ml-1">You</span>
                      )}
                      {p.has_pending_catchup && (
                        <span className="text-xs bg-orange-500/30 text-orange-300 px-1.5 py-0.5 rounded-full ml-1">Catch Up</span>
                      )}
                    </p>
                    <p className="text-gray-400 text-sm">
                      {p.current_streak} day streak • ${Math.round(p.total_saved || 0)} saved
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {p.today_status === 'saved' && (
                    <motion.span className="text-2xl" initial={{ scale: 0 }} animate={{ scale: [1, 1.3, 1] }} transition={{ repeat: 2, duration: 0.3 }}>
                      ✅
                    </motion.span>
                  )}
                  {p.today_status === 'resting' && <span className="text-xl">😴</span>}
                  {p.today_status === 'none' && <span className="text-gray-500 text-sm">Pending</span>}
                  {p.vault_amount > 0 && (
                    <span className="text-xs bg-purple-500/30 text-purple-300 px-2 py-1 rounded-full">
                      Vault: ${Math.round(p.vault_amount)}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Progress Bar under each participant */}
              <div className="w-full">
                <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
                  {/* Withdrawn indicator (shows what was lost) */}
                  {p.total_withdrawn > 0 && (
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(progress + withdrawnProgress, 100)}%` }}
                      className="absolute top-0 left-0 h-full bg-orange-500/30 rounded-full"
                    />
                  )}
                  {/* Current progress */}
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full"
                  />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500">{progress.toFixed(0)}%</span>
                  <span className="text-xs text-gray-500">${Math.round(challengeGoal)}</span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ==================== SKIP MODAL ====================
function SkipModal({ isOpen, onClose, onConfirm, amount }) {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl text-center"
        onClick={e => e.stopPropagation()}
      >
        <div className="text-6xl mb-4">🤝</div>
        <h2 className="text-2xl font-black text-gray-900 mb-4">
          No Sweat! We Got You.
        </h2>
        <p className="text-gray-600 mb-6 leading-relaxed">
          Life happens, and sometimes the math ain't mathing today. We've paused your streak 
          so you don't lose your Street Cred. This envelope has been safely moved to your 
          Catch-Up Vault. Handle your business, and clear it out when you get your next bag! 💼
        </p>
        
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
          <p className="text-purple-800 font-medium">
            ${Math.round(amount || 0)} → Catch-Up Vault
          </p>
        </div>

        <motion.button
          onClick={onConfirm}
          className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-lg rounded-xl"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Send to Vault 🔒
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

// ==================== CATCH-UP VAULT ====================
function CatchUpVault({ vault, onClear, isLoading }) {
  if (!vault || vault.vault_amount === 0) return null;

  return (
    <motion.div 
      className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-5 shadow-xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
        <span>🔒</span> Catch-Up Vault
      </h3>
      
      <div className="bg-white/10 rounded-xl p-4 mb-4">
        <p className="text-white/80 text-sm mb-1">Total Owed</p>
        <p className="text-white font-black text-3xl">${Math.round(vault.vault_amount)}</p>
        <p className="text-white/60 text-sm mt-1">
          {vault.vault_envelopes?.length || 0} envelope{vault.vault_envelopes?.length !== 1 ? 's' : ''} skipped
        </p>
      </div>

      {vault.streak_frozen && (
        <p className="text-amber-300 text-sm mb-4 flex items-center gap-1">
          <span>⏸️</span> Streak paused until vault is cleared
        </p>
      )}

      <motion.button
        onClick={onClear}
        disabled={isLoading}
        className="w-full py-3 bg-white text-purple-700 font-bold rounded-xl disabled:opacity-50"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {isLoading ? 'Processing...' : 'Clear Vault & Restore Streak 💪'}
      </motion.button>
    </motion.div>
  );
}

// ==================== WITHDRAW MODAL ====================
function WithdrawModal({ isOpen, onClose, onWithdraw, currentSaved, goal, isLoading }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  
  const withdrawAmount = parseFloat(amount) || 0;
  const maxWithdraw = currentSaved || 0;
  const newTotal = Math.max(0, currentSaved - withdrawAmount);
  const progressBefore = goal > 0 ? (currentSaved / goal * 100) : 0;
  const progressAfter = goal > 0 ? (newTotal / goal * 100) : 0;
  
  const handleWithdraw = () => {
    if (withdrawAmount <= 0) {
      toast.error('Please enter an amount');
      return;
    }
    if (withdrawAmount > maxWithdraw) {
      toast.error(`Can't withdraw more than $${Math.round(maxWithdraw)}`);
      return;
    }
    onWithdraw(withdrawAmount, reason);
    setAmount('');
    setReason('');
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="text-center mb-4">
          <div className="w-14 h-14 mx-auto mb-3 bg-orange-100 rounded-2xl flex items-center justify-center">
            <span className="text-3xl">💸</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Withdraw Funds</h2>
          <p className="text-gray-500 text-sm mt-1">Life happens. We understand. 💪</p>
        </div>
        
        {/* Current Balance */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
          <p className="text-emerald-700 text-sm">Available to Withdraw</p>
          <p className="text-emerald-900 font-black text-2xl">${Math.round(maxWithdraw)}</p>
        </div>
        
        {/* Amount Input */}
        <div className="mb-4">
          <label className="text-gray-700 font-medium mb-2 block">Amount to Withdraw</label>
          <input
            type="number"
            placeholder="Enter amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            max={maxWithdraw}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-xl font-bold focus:border-orange-500 focus:outline-none"
          />
        </div>
        
        {/* Optional Reason */}
        <div className="mb-4">
          <label className="text-gray-700 font-medium mb-2 block">Reason (optional)</label>
          <input
            type="text"
            placeholder="Emergency, bill payment, etc."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none"
          />
        </div>
        
        {/* Progress Impact Preview */}
        {withdrawAmount > 0 && withdrawAmount <= maxWithdraw && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
            <p className="text-orange-800 text-sm font-medium mb-2">Impact on Your Goal</p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Before:</span>
                <span className="font-bold text-emerald-600">{progressBefore.toFixed(0)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all"
                  style={{ width: `${progressBefore}%` }}
                />
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">After:</span>
                <span className="font-bold text-orange-600">{progressAfter.toFixed(0)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full transition-all"
                  style={{ width: `${progressAfter}%` }}
                />
              </div>
            </div>
            <p className="text-orange-700 text-xs mt-2">
              Don't worry - you can catch up later! 💪
            </p>
          </div>
        )}
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleWithdraw}
            disabled={isLoading || withdrawAmount <= 0 || withdrawAmount > maxWithdraw}
            className="flex-1 py-3 px-4 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 disabled:opacity-50 transition-colors"
          >
            {isLoading ? 'Processing...' : 'Withdraw'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ==================== CATCH-UP WITHDRAWAL PANEL ====================
function CatchUpWithdrawalPanel({ totalWithdrawn, onCatchUp, goal, currentSaved, isLoading }) {
  const [catchUpAmount, setCatchUpAmount] = useState('');
  const [showInput, setShowInput] = useState(false);
  
  if (!totalWithdrawn || totalWithdrawn <= 0) return null;
  
  const handleCatchUp = () => {
    const amount = parseFloat(catchUpAmount) || 0;
    if (amount <= 0) {
      toast.error('Please enter an amount');
      return;
    }
    if (amount > totalWithdrawn) {
      toast.error(`Maximum catch-up amount is $${Math.round(totalWithdrawn)}`);
      return;
    }
    onCatchUp(amount);
    setCatchUpAmount('');
    setShowInput(false);
  };
  
  // Calculate progress
  const progressWithCatchUp = goal > 0 ? ((currentSaved + totalWithdrawn) / goal * 100) : 0;
  const currentProgress = goal > 0 ? (currentSaved / goal * 100) : 0;
  
  return (
    <motion.div 
      className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl p-5 shadow-xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
        <span>🔄</span> Catch Up Your Savings
      </h3>
      
      <div className="bg-white/10 rounded-xl p-4 mb-4">
        <p className="text-white/80 text-sm mb-1">Previously Withdrawn</p>
        <p className="text-white font-black text-3xl">${Math.round(totalWithdrawn)}</p>
        <p className="text-white/60 text-sm mt-2">
          Pay it back to restore your goal progress!
        </p>
      </div>
      
      {/* Progress visualization */}
      <div className="bg-white/10 rounded-xl p-3 mb-4">
        <div className="flex justify-between text-xs text-white/70 mb-1">
          <span>Current: {currentProgress.toFixed(0)}%</span>
          <span>Potential: {progressWithCatchUp.toFixed(0)}%</span>
        </div>
        <div className="h-3 bg-white/20 rounded-full overflow-hidden relative">
          {/* Potential progress (lighter) */}
          <div 
            className="absolute top-0 left-0 h-full bg-white/30 rounded-full"
            style={{ width: `${Math.min(progressWithCatchUp, 100)}%` }}
          />
          {/* Current progress */}
          <div 
            className="absolute top-0 left-0 h-full bg-white rounded-full"
            style={{ width: `${Math.min(currentProgress, 100)}%` }}
          />
        </div>
      </div>
      
      {!showInput ? (
        <motion.button
          onClick={() => setShowInput(true)}
          className="w-full py-3 bg-white text-orange-600 font-bold rounded-xl"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Catch Up Now 💪
        </motion.button>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Enter amount"
              value={catchUpAmount}
              onChange={(e) => setCatchUpAmount(e.target.value)}
              max={totalWithdrawn}
              className="flex-1 px-4 py-3 rounded-xl text-black font-bold focus:outline-none"
            />
            <button
              onClick={() => setCatchUpAmount(totalWithdrawn.toString())}
              className="px-3 py-2 bg-white/20 text-white rounded-xl text-sm font-medium"
            >
              Max
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowInput(false)}
              className="flex-1 py-3 bg-white/20 text-white font-medium rounded-xl"
            >
              Cancel
            </button>
            <button
              onClick={handleCatchUp}
              disabled={isLoading}
              className="flex-1 py-3 bg-white text-orange-600 font-bold rounded-xl disabled:opacity-50"
            >
              {isLoading ? '...' : 'Pay Back'}
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ==================== INVITE MODAL ====================
function InviteModal({ isOpen, onClose, onInvite, challengeName, isLoading }) {
  const [email, setEmail] = useState('');
  
  const handleInvite = () => {
    console.log('[INVITE DEBUG - Frontend] 1. Initiating invite for:', email);
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    onInvite(email.trim());
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-white rounded-2xl p-6 w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold mb-2">Invite Friends</h3>
        <p className="text-gray-500 text-sm mb-4">Invite someone to join "{challengeName}"</p>
        
        <input
          type="email"
          placeholder="Friend's email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-4 focus:border-amber-500 focus:outline-none"
        />
        
        <div className="flex gap-3">
          <button 
            onClick={onClose} 
            className="flex-1 py-3 border border-gray-300 rounded-xl font-medium"
          >
            Cancel
          </button>
          <button 
            onClick={handleInvite} 
            disabled={isLoading || !email.trim()}
            className="flex-1 py-3 bg-amber-500 text-black font-bold rounded-xl disabled:opacity-50"
          >
            {isLoading ? 'Sending...' : 'Send Invite'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ==================== USER NOT FOUND MODAL (Native Share Fallback) ====================
function UserNotFoundModal({ isOpen, email, challengeName, onClose }) {
  const handleSendText = () => {
    console.log('[INVITE DEBUG - Frontend] Triggering SMS native share for:', email);
    const message = encodeURIComponent(
      `Hey! I'm doing the "${challengeName}" savings challenge on GuapGoals. Join me and let's stack some cash! 📨💰 Download: https://guapgoals.com`
    );
    window.open(`sms:?body=${message}`, '_blank');
    toast.success('Opening SMS...');
    onClose();
  };
  
  const handleSendEmail = () => {
    console.log('[INVITE DEBUG - Frontend] Triggering Email native share for:', email);
    const subject = encodeURIComponent(`Join my savings challenge on GuapGoals!`);
    const body = encodeURIComponent(
      `Hey!\n\nI'm doing the "${challengeName}" savings challenge on GuapGoals. Join me and let's stack some cash! 📨💰\n\nDownload the app: https://guapgoals.com`
    );
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
    toast.success('Opening email...');
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
            <span className="text-3xl">🔍</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900">User Not Found</h2>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6">
          <p className="text-amber-800 text-center">
            <span className="font-semibold">{email}</span> isn't on GuapGoals yet!
          </p>
        </div>

        <p className="text-sm text-gray-500 text-center mb-6">
          Send them an invite to download the app and join your challenge!
        </p>

        <div className="flex gap-3">
          <button
            onClick={handleSendText}
            className="flex-1 py-3 px-4 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
          >
            <span>💬</span> Text
          </button>
          <button
            onClick={handleSendEmail}
            className="flex-1 py-3 px-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
          >
            <span>📧</span> Email
          </button>
        </div>

        <button
          onClick={onClose}
          className="w-full mt-4 py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
        >
          Cancel
        </button>
      </motion.div>
    </motion.div>
  );
}

// ==================== CHALLENGE COMPLETION CELEBRATION ====================
function CompletionCelebration({ isOpen, onClose, challengeData, stats }) {
  const canShareNative = typeof navigator !== 'undefined' && navigator.share;
  
  useEffect(() => {
    if (isOpen) {
      // Trigger massive confetti
      const duration = 3000;
      const end = Date.now() + duration;
      
      const colors = ['#FFD700', '#00FF00', '#FF6B6B', '#4ECDC4', '#9B59B6'];
      
      (function frame() {
        confetti({
          particleCount: 5,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors
        });
        confetti({
          particleCount: 5,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors
        });
        
        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      })();
      
      // Haptic feedback
      triggerHaptic('heavy');
    }
  }, [isOpen]);
  
  const handleShare = async () => {
    const shareText = `🎉 I just completed the "${challengeData?.name}" ${challengeData?.duration}-day Envelope Challenge on GuapGoals!\n\n💰 Total Saved: $${stats?.total_saved || 0}\n🔥 Longest Streak: ${stats?.longest_streak || 0} days\n📈 Completion: ${stats?.completion_percentage || 100}%\n\nJoin me and start your savings journey! 💪`;
    
    if (canShareNative) {
      try {
        await navigator.share({
          title: 'I Completed the Envelope Challenge! 🎉',
          text: shareText,
          url: 'https://guapgoals.com'
        });
      } catch (e) {
        // User cancelled or error
      }
    } else {
      // Copy to clipboard
      await navigator.clipboard.writeText(shareText);
      toast.success('Achievement copied to clipboard!');
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.5, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.5, y: 50 }}
        transition={{ type: 'spring', damping: 15 }}
        className="bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 rounded-3xl p-1 w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="bg-slate-900 rounded-[22px] p-6 text-center">
          {/* Trophy */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="text-7xl mb-4"
          >
            🏆
          </motion.div>
          
          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl font-black text-white mb-2"
          >
            CHALLENGE COMPLETE!
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-amber-400 font-bold text-lg mb-6"
          >
            "{challengeData?.name}" - {challengeData?.duration} Days
          </motion.p>
          
          {/* Stats Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white/10 rounded-2xl p-4 mb-6"
          >
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-3xl font-black text-emerald-400">${stats?.total_saved || 0}</p>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Total Saved</p>
              </div>
              <div>
                <p className="text-3xl font-black text-amber-400">{stats?.longest_streak || 0}</p>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Best Streak</p>
              </div>
              <div>
                <p className="text-3xl font-black text-purple-400">{stats?.completion_percentage || 100}%</p>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Completed</p>
              </div>
            </div>
          </motion.div>
          
          {/* Achievement Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
            className="inline-block mb-6"
          >
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold px-6 py-2 rounded-full text-sm">
              🎖️ ENVELOPE MASTER - {challengeData?.duration} DAYS
            </div>
          </motion.div>
          
          {/* Share Button */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            onClick={handleShare}
            className="w-full py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold text-lg rounded-2xl mb-3 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all"
          >
            📤 Share Achievement
          </motion.button>
          
          <button
            onClick={onClose}
            className="w-full py-3 text-gray-400 hover:text-white transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ==================== MAIN COMPONENT ====================
export default function EnvelopeChallengeV2() {
  // Envelope context for global state sync
  const { markEnvelopeSaved, markEnvelopeSkipped, refreshData: refreshEnvelopeContext } = useEnvelope();
  
  // Flow state
  const [flowStep, setFlowStep] = useState('loading'); // 'loading' | 'instructions' | 'setup' | 'dashboard'
  
  // Data state
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [challengeData, setChallengeData] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showUserNotFound, setShowUserNotFound] = useState(false);
  const [notFoundEmail, setNotFoundEmail] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCompletionCelebration, setShowCompletionCelebration] = useState(false);
  const [completionStats, setCompletionStats] = useState(null);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  
  const wsRef = useRef(null);

  // Load current user
  useEffect(() => {
    const loadUser = async () => {
      try {
        const response = await apiGet('/auth/me');
        if (response.ok) {
          const user = await response.json();
          setCurrentUser(user);
        }
      } catch (e) {
        console.error('Failed to load user:', e);
      }
    };
    loadUser();
  }, []);

  // Load existing challenges
  const loadChallenges = useCallback(async () => {
    try {
      const response = await apiGet('/envelope-v2/challenges');
      if (response.ok) {
        const data = await response.json();
        setChallenges(data.challenges || []);
        
        if (data.challenges?.length > 0) {
          // User has existing challenges - go to dashboard
          setSelectedChallenge(data.challenges[0]);
          setFlowStep('dashboard');
        } else {
          // No challenges - show instructions for new user
          setFlowStep('instructions');
        }
      }
    } catch (e) {
      console.error('Failed to load challenges:', e);
      setFlowStep('instructions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChallenges();
  }, [loadChallenges]);

  // Load challenge details
  const loadChallengeData = useCallback(async (challengeId) => {
    if (!challengeId) return;
    try {
      const response = await apiGet(`/envelope-v2/challenges/${challengeId}`);
      if (response.ok) {
        const data = await response.json();
        setChallengeData(data);
      }
    } catch (e) {
      console.error('Failed to load challenge:', e);
    }
  }, []);

  useEffect(() => {
    if (selectedChallenge?.id && flowStep === 'dashboard') {
      loadChallengeData(selectedChallenge.id);
    }
  }, [selectedChallenge, flowStep, loadChallengeData]);

  // WebSocket connection
  useEffect(() => {
    if (!selectedChallenge?.id || !currentUser?.user_id || flowStep !== 'dashboard') return;

    const connectWS = () => {
      const ws = new WebSocket(`${WS_URL}/api/envelope-v2/ws/${selectedChallenge.id}/${currentUser.user_id}`);
      
      ws.onopen = () => console.log('✅ Envelope WS connected');
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (e) {
          console.error('WS parse error:', e);
        }
      };
      
      ws.onclose = () => {
        console.log('Envelope WS closed');
        setTimeout(connectWS, 3000);
      };
      
      wsRef.current = ws;
    };

    connectWS();

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [selectedChallenge?.id, currentUser?.user_id, flowStep]);

  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'envelope_revealed':
        setChallengeData(prev => prev ? { ...prev, today_envelope: message.envelope, challenge: { ...prev.challenge, today_revealed: true } } : prev);
        toast.info(`${message.revealed_by} revealed today's envelope!`);
        break;
      case 'envelope_saved':
        if (message.animate) triggerHaptic('light');
        setChallengeData(prev => {
          if (!prev) return prev;
          const updatedBoard = prev.guap_board.map(p => p.user_id === message.user_id ? { ...p, today_status: 'saved', current_streak: message.new_streak, is_hot: message.is_hot } : p);
          return { ...prev, guap_board: updatedBoard };
        });
        toast.success(`${message.user_name} saved $${Math.round(message.amount)}! 🎉`);
        break;
      case 'status_update':
        setChallengeData(prev => {
          if (!prev) return prev;
          const updatedBoard = prev.guap_board.map(p => p.user_id === message.user_id ? { ...p, today_status: message.today_status } : p);
          return { ...prev, guap_board: updatedBoard };
        });
        break;
      case 'member_joined':
        toast.success(message.message);
        loadChallengeData(selectedChallenge.id);
        break;
      case 'vault_cleared':
        toast.success(message.message);
        loadChallengeData(selectedChallenge.id);
        break;
      case 'challenge_completed':
        // Show completion celebration!
        if (message.trigger_celebration) {
          const myStats = message.completion_stats?.find(s => s.user_id === currentUser?.user_id);
          setCompletionStats({
            total_saved: myStats?.total_saved || 0,
            longest_streak: myStats?.longest_streak || 0,
            completion_percentage: myStats ? Math.round((myStats.envelopes_saved / message.duration) * 100) : 100
          });
          setShowCompletionCelebration(true);
        }
        loadChallengeData(selectedChallenge.id);
        break;
      case 'challenge_deleted':
        toast.info('This challenge has been deleted by the owner');
        loadChallenges();
        break;
      default:
        console.log('Unknown WS message:', message.type);
    }
  };

  // Create new challenge
  const handleCreateChallenge = async (config) => {
    setActionLoading(true);
    console.log('[CREATE DEBUG] Creating challenge with config:', config);
    
    try {
      const response = await apiPost('/envelope-v2/challenges', config);
      if (response.ok) {
        const data = await response.json();
        console.log('[CREATE DEBUG] Challenge created:', data);
        setChallenges(prev => [...prev, data.challenge]);
        setSelectedChallenge(data.challenge);
        setFlowStep('dashboard');
        triggerConfetti();
        toast.success('Challenge created! Let\'s go! 🚀');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create challenge');
      }
    } catch (e) {
      console.error('[CREATE DEBUG] Error:', e);
      toast.error('Error creating challenge');
    } finally {
      setActionLoading(false);
    }
  };

  // Reveal envelope
  const handleReveal = async () => {
    if (!selectedChallenge?.id) return;
    setActionLoading(true);
    try {
      const response = await apiPost(`/envelope-v2/challenges/${selectedChallenge.id}/reveal`);
      if (response.ok) {
        const data = await response.json();
        setChallengeData(prev => ({ ...prev, today_envelope: data.envelope, challenge: { ...prev.challenge, today_revealed: true } }));
      }
    } catch (e) {
      toast.error('Failed to reveal envelope');
    } finally {
      setActionLoading(false);
    }
  };

  // Save envelope
  const handleSave = async () => {
    if (!selectedChallenge?.id) return;
    setActionLoading(true);
    try {
      const response = await apiPost(`/envelope-v2/challenges/${selectedChallenge.id}/save`);
      if (response.ok) {
        const data = await response.json();
        if (data.trigger_confetti) triggerConfetti();
        if (data.trigger_haptic) triggerHaptic('heavy');
        toast.success(`Saved $${Math.round(data.amount)}! 🎉 ${data.new_streak} day streak!`);
        setChallengeData(prev => ({ ...prev, my_progress: { ...prev.my_progress, today_status: 'saved', current_streak: data.new_streak, total_saved: data.total_saved } }));
        loadChallengeData(selectedChallenge.id);
        
        // Update global context for Dashboard real-time sync
        markEnvelopeSaved(data.amount, data.new_streak, data.total_saved);
        refreshEnvelopeContext();
        
        // Check if this was the last envelope (challenge complete)
        await checkCompletionStatus();
      }
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Check if challenge is complete
  const checkCompletionStatus = async () => {
    if (!selectedChallenge?.id) return;
    try {
      const response = await apiGet(`/envelope-v2/challenges/${selectedChallenge.id}/completion-status`);
      if (response.ok) {
        const data = await response.json();
        if (data.is_completed && data.trigger_celebration) {
          setCompletionStats(data.my_stats);
          setShowCompletionCelebration(true);
        }
      }
    } catch (e) {
      console.error('Failed to check completion:', e);
    }
  };

  // Skip envelope
  const confirmSkip = async () => {
    if (!selectedChallenge?.id) return;
    setActionLoading(true);
    try {
      const response = await apiPost(`/envelope-v2/challenges/${selectedChallenge.id}/skip`);
      if (response.ok) {
        const data = await response.json();
        toast.info('Sent to Vault 🔒');
        setShowSkipModal(false);
        loadChallengeData(selectedChallenge.id);
        
        // Update global context for Dashboard sync
        markEnvelopeSkipped(data.vault_amount || 0);
        refreshEnvelopeContext();
      }
    } catch (e) {
      toast.error('Failed to skip');
    } finally {
      setActionLoading(false);
    }
  };

  // Clear vault
  const handleClearVault = async () => {
    if (!selectedChallenge?.id) return;
    setActionLoading(true);
    try {
      const response = await apiPost(`/envelope-v2/challenges/${selectedChallenge.id}/vault/clear`);
      if (response.ok) {
        const data = await response.json();
        if (data.trigger_celebration) triggerConfetti();
        triggerHaptic('heavy');
        toast.success(data.message);
        loadChallengeData(selectedChallenge.id);
      }
    } catch (e) {
      toast.error('Failed to clear vault');
    } finally {
      setActionLoading(false);
    }
  };

  // Withdraw from savings
  const handleWithdraw = async (amount, reason) => {
    if (!selectedChallenge?.id) return;
    setActionLoading(true);
    try {
      const response = await apiPost(`/envelope-v2/challenges/${selectedChallenge.id}/withdraw`, { 
        amount, 
        reason 
      });
      if (response.ok) {
        const data = await response.json();
        toast.info(data.message);
        setShowWithdrawModal(false);
        loadChallengeData(selectedChallenge.id);
        refreshEnvelopeContext();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to withdraw');
      }
    } catch (e) {
      toast.error('Error processing withdrawal');
    } finally {
      setActionLoading(false);
    }
  };

  // Catch up on withdrawals
  const handleCatchUp = async (amount) => {
    if (!selectedChallenge?.id) return;
    setActionLoading(true);
    try {
      const response = await apiPost(`/envelope-v2/challenges/${selectedChallenge.id}/catch-up`, { amount });
      if (response.ok) {
        const data = await response.json();
        if (data.trigger_celebration) triggerConfetti();
        triggerHaptic('heavy');
        toast.success(data.message);
        loadChallengeData(selectedChallenge.id);
        refreshEnvelopeContext();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to catch up');
      }
    } catch (e) {
      toast.error('Error processing catch-up');
    } finally {
      setActionLoading(false);
    }
  };

  // Invite handler with debugging
  const handleInvite = async (email) => {
    if (!selectedChallenge?.id) return;
    setActionLoading(true);
    
    console.log('[INVITE DEBUG - Frontend] 2. Sending invite request to backend...');
    
    try {
      const response = await apiPost(`/envelope-v2/challenges/${selectedChallenge.id}/invite`, { email });
      const data = await response.json();
      
      console.log('[INVITE DEBUG - Frontend] 3. Backend response:', data);
      
      if (response.ok) {
        if (data.user_found === false || data.error_code === 'USER_NOT_FOUND') {
          console.log('[INVITE DEBUG - Frontend] 4. User not found, showing native share modal');
          setNotFoundEmail(email);
          setShowUserNotFound(true);
        } else {
          console.log('[INVITE DEBUG - Frontend] 4. Invite sent successfully');
          toast.success(`Invitation sent to ${email}!`);
        }
        setShowInviteModal(false);
      } else {
        console.error('[INVITE DEBUG - Frontend] 4. Backend error:', data.detail);
        toast.error(data.detail || 'Failed to send invitation');
      }
    } catch (e) {
      console.error('[INVITE DEBUG - Frontend] ERROR:', e);
      toast.error('Error sending invitation');
    } finally {
      setActionLoading(false);
    }
  };

  // Delete challenge handler (owner only)
  const handleDeleteChallenge = async () => {
    if (!selectedChallenge?.id) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/envelope-v2/challenges/${selectedChallenge.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Challenge deleted successfully');
        setShowDeleteModal(false);
        setSelectedChallenge(null);
        setChallengeData(null);
        // Reload challenges list
        await loadChallenges();
        // If no challenges left, go to instructions
        const remaining = challenges.filter(c => c.id !== selectedChallenge.id);
        if (remaining.length === 0) {
          setFlowStep('instructions');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete challenge');
      }
    } catch (e) {
      console.error('Delete challenge error:', e);
      toast.error('Error deleting challenge');
    } finally {
      setActionLoading(false);
    }
  };

  // Check if current user is owner of selected challenge
  const isOwner = selectedChallenge?.owner_id === currentUser?.user_id;

  // Loading state
  if (loading || flowStep === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // STEP 1: Instructions
  if (flowStep === 'instructions') {
    return <InstructionsScreen onNext={() => setFlowStep('setup')} />;
  }

  // STEP 2: Setup/Options
  if (flowStep === 'setup') {
    return (
      <SetupScreen
        onBack={() => challenges.length > 0 ? setFlowStep('dashboard') : setFlowStep('instructions')}
        onCreateChallenge={handleCreateChallenge}
        isLoading={actionLoading}
      />
    );
  }

  // STEP 3: Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-black text-white flex items-center gap-2">
          <span>📨</span> Envelope Challenge
        </h1>
        <button
          onClick={() => setFlowStep('setup')}
          className="px-4 py-2 bg-amber-500 text-black font-bold rounded-xl"
        >
          + New
        </button>
      </div>

      {/* Challenge Selector */}
      {challenges.length > 1 && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {challenges.map(c => (
            <button
              key={c.id}
              onClick={() => setSelectedChallenge(c)}
              className={`px-4 py-2 rounded-xl whitespace-nowrap font-medium transition-all ${
                selectedChallenge?.id === c.id ? 'bg-amber-500 text-black' : 'bg-white/10 text-white'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      {challengeData && (
        <div className="space-y-6">
          {/* Progress Header */}
          <div className="bg-white/10 rounded-2xl p-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-white font-bold text-lg">{challengeData.challenge?.name}</h2>
              <div className="flex items-center gap-3">
                {isOwner && (
                  <button 
                    onClick={() => setShowDeleteModal(true)} 
                    className="text-red-400 hover:text-red-300 text-sm font-medium"
                    data-testid="delete-challenge-btn"
                  >
                    🗑️ Delete
                  </button>
                )}
                <button onClick={() => setShowInviteModal(true)} className="text-amber-400 text-sm font-medium">
                  + Invite
                </button>
              </div>
            </div>
            <div className="flex gap-4 text-sm">
              <div className="text-gray-300">
                Goal: <span className="text-white font-bold">${Math.round(challengeData.challenge?.total_goal || 0)}</span>
              </div>
              <div className="text-gray-300">
                Day: <span className="text-white font-bold">{challengeData.challenge?.current_day || 1}/{challengeData.challenge?.duration}</span>
              </div>
            </div>
          </div>

          {/* Daily Envelope */}
          <div className="py-6">
            <DailyEnvelope
              envelope={challengeData.today_envelope}
              onReveal={handleReveal}
              onSave={handleSave}
              onSkip={() => setShowSkipModal(true)}
              isRevealed={challengeData.today_envelope?.is_revealed || challengeData.challenge?.today_revealed}
              isLoading={actionLoading}
              todayStatus={challengeData.my_progress?.today_status || 'none'}
            />
          </div>

          {/* Catch-Up Vault */}
          <CatchUpVault
            vault={challengeData.my_progress}
            onClear={handleClearVault}
            isLoading={actionLoading}
          />

          {/* Catch-Up Withdrawal Panel (for previous withdrawals) */}
          <CatchUpWithdrawalPanel
            totalWithdrawn={challengeData.my_progress?.total_withdrawn || 0}
            onCatchUp={handleCatchUp}
            goal={challengeData.challenge?.total_goal || 0}
            currentSaved={challengeData.my_progress?.total_saved || 0}
            isLoading={actionLoading}
          />

          {/* Withdraw Button */}
          {(challengeData.my_progress?.total_saved || 0) > 0 && (
            <motion.button
              onClick={() => setShowWithdrawModal(true)}
              className="w-full py-3 px-4 bg-white/10 border border-white/20 text-white/80 font-medium rounded-xl flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              data-testid="withdraw-btn"
            >
              <span>💸</span> Need to Withdraw?
            </motion.button>
          )}

          {/* Guap Board */}
          <GuapBoard
            participants={challengeData.guap_board || []}
            currentUserId={currentUser?.user_id}
            challengeGoal={challengeData.challenge?.total_goal || 0}
          />

          {/* Challenge Progress Grid */}
          <ChallengeProgressGrid
            envelopes={challengeData.all_envelopes || []}
            totalDays={challengeData.challenge?.duration || 30}
            currentDay={challengeData.challenge?.current_day || 1}
            className="mt-6"
          />
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showSkipModal && (
          <SkipModal
            isOpen={showSkipModal}
            onClose={() => setShowSkipModal(false)}
            onConfirm={confirmSkip}
            amount={challengeData?.today_envelope?.amount}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showWithdrawModal && (
          <WithdrawModal
            isOpen={showWithdrawModal}
            onClose={() => setShowWithdrawModal(false)}
            onWithdraw={handleWithdraw}
            currentSaved={challengeData?.my_progress?.total_saved || 0}
            goal={challengeData?.challenge?.total_goal || 0}
            isLoading={actionLoading}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showInviteModal && (
          <InviteModal
            isOpen={showInviteModal}
            onClose={() => setShowInviteModal(false)}
            onInvite={handleInvite}
            challengeName={challengeData?.challenge?.name}
            isLoading={actionLoading}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showUserNotFound && (
          <UserNotFoundModal
            isOpen={showUserNotFound}
            email={notFoundEmail}
            challengeName={challengeData?.challenge?.name}
            onClose={() => setShowUserNotFound(false)}
          />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center mb-4">
                <div className="w-14 h-14 mx-auto mb-3 bg-red-100 rounded-2xl flex items-center justify-center">
                  <span className="text-3xl">🗑️</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Delete Challenge?</h2>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <p className="text-red-800 text-sm text-center">
                  This will permanently delete <span className="font-bold">"{selectedChallenge?.name}"</span> and all progress. This action cannot be undone.
                </p>
              </div>
              
              <p className="text-gray-500 text-sm text-center mb-6">
                All participants will lose access to this challenge.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteChallenge}
                  disabled={actionLoading}
                  className="flex-1 py-3 px-4 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 disabled:opacity-50 transition-colors"
                  data-testid="confirm-delete-btn"
                >
                  {actionLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Challenge Completion Celebration */}
      <AnimatePresence>
        {showCompletionCelebration && (
          <CompletionCelebration
            isOpen={showCompletionCelebration}
            onClose={() => setShowCompletionCelebration(false)}
            challengeData={challengeData?.challenge}
            stats={completionStats}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
